import {
  CountryISOCode,
  PartnerCRMProperty,
  PropertyType,
} from "@justhome/api-core";
import { formatMoney, formatPropertyType } from "@justhome/common";
import { Skeleton } from "@justhome/react-ui";
import useLanguage from "@lib/hooks/useLanguage";
import clsx from "clsx";
import Image from "next/image";
import { FC } from "react";

interface Property {
  title?: string;
  postalCode?: string;
  city?: string;
  street?: string;
  country?: CountryISOCode;
  purchasePrice?: number;
  imageUrl?: string;
  propertyType?: string;
  livingArea?: number;
  numberOfParkingSpaces?: number;
  pricePerParkingSpace?: number;
}

interface PropertyCardProps {
  property?: PartnerCRMProperty;
  isLoading: boolean;
  className?: string;
}

const TinyPropertyCard: FC<PropertyCardProps> = ({
  property,
  isLoading,
  className,
}) => {
  return (
    <section className={clsx("flex gap-s items-center", className)}>
      <div className="w-[160px] aspect-[4/3] flex-none relative">
        <PropertyImage
          imageUrl={property?.thumbnailImageUrl ?? property?.imageUrl}
          isLoading={isLoading}
        />
      </div>
      <div className="flex flex-col grow gap-xs">
        <PropertyMainInfo {...property} isLoading={isLoading} />
        <PropertyDetails {...property} />
      </div>
    </section>
  );
};

interface PropertyImageProps {
  isLoading: boolean;
  imageUrl?: string;
}

const PropertyImage = ({ imageUrl, isLoading }: PropertyImageProps) => {
  return (
    <Skeleton
      width="100%"
      height="100%"
      className="absolute inset-none"
      isLoading={isLoading}
    >
      {imageUrl && (
        <div className="relative h-full w-full rounded-s overflow-hidden">
          <Image
            loader={({ src }) => src}
            src={imageUrl}
            fill
            alt=""
            className="object-cover"
          />
        </div>
      )}
    </Skeleton>
  );
};

interface PropertyMainInfo
  extends Pick<Property, "title" | "postalCode" | "city" | "street"> {
  isLoading: boolean;
}

const PropertyMainInfo = ({
  isLoading,
  title,
  street,
  postalCode,
  city,
}: PropertyMainInfo) => {
  return (
    <div>
      <h3 className="text-body-2 font-semibold">
        <Skeleton isLoading={isLoading} height={16} width="100%">
          {title}
        </Skeleton>
      </h3>
      <Skeleton isLoading={isLoading} height={16} width={200}>
        <p className="text-title-1 text-neutral-675">
          {street}, {postalCode} {city}
        </p>
      </Skeleton>
    </div>
  );
};

type PropertyDetailsProps = Pick<
  Property,
  "livingArea" | "purchasePrice" | "propertyType"
>;

const PropertyDetails = ({
  livingArea,
  propertyType,
  purchasePrice,
}: PropertyDetailsProps) => {
  const locale = useLanguage();
  const items: string[] = [];
  if (purchasePrice) {
    items.push(formatMoney(purchasePrice, true, locale));
  }
  if (livingArea) {
    items.push(livingArea + (locale === "en" ? " sqm" : " m²"));
  }
  if (propertyType) {
    items.push(formatPropertyType(propertyType as PropertyType, locale));
  }

  return (
    <div className="flex flex-col gap-2xs text-title-1 text-primary-800">
      <span>{items.join(" • ")}</span>
    </div>
  );
};

export default TinyPropertyCard;
